/*
  Export-Dialog für den Schichtplan V2: PDF- und Excel-Export mit Live-Vorschau.

  Hülle ist ShiftplanDialog – derselbe DS-Dialog wie Einstellungen, Zeitraum
  kopieren und Solver. Die Bedienelemente sind DS-Komponenten (AppSwitch,
  AppCheckbox, AppInput, AppField, AppButton); lokal bleibt nur der
  Segment-Umschalter, für den das DS keine Komponente hat.
*/

import React, { useCallback, useEffect, useMemo, useState } from "react";
import CheckOutlinedIcon from "@mui/icons-material/CheckOutlined";
import CropLandscapeOutlinedIcon from "@mui/icons-material/CropLandscapeOutlined";
import CropPortraitOutlinedIcon from "@mui/icons-material/CropPortraitOutlined";
import FileDownloadOutlinedIcon from "@mui/icons-material/FileDownloadOutlined";
import GridOnOutlinedIcon from "@mui/icons-material/GridOnOutlined";
import PictureAsPdfOutlinedIcon from "@mui/icons-material/PictureAsPdfOutlined";
import AppButton from "../../../../design-system/components/AppButton";
import AppCheckbox from "../../../../design-system/components/AppCheckbox";
import AppInput from "../../../../design-system/components/AppInput";
import AppSwitch from "../../../../design-system/components/AppSwitch";
import AppText from "../../../../design-system/components/AppText";
import { IconChevronLeft, IconChevronRight } from "../../../../design-system/icons";
import ShiftplanDialog from "../ui/ShiftplanDialog";
import {
  addMonthsToDate,
  addWeeksToDate,
  formatMonthTitle,
  formatWeekTitle,
  getMonthDays,
  getWeekDays,
} from "../../domain/date/month";
import { buildShiftplanExportModel, DEFAULT_CELL_DISPLAY } from "../../domain/model/exportModel";
import { groupPublicHolidayNamesByDate } from "../../domain/model/selectors";
import usePlanRange from "../../domain/model/usePlanRange";
import { EXPORT_FORMATS, normalizeExportFormat } from "./exportFormats";
import ExportPreview from "./ExportPreview";
import { downloadShiftplanExcel } from "./excel/buildShiftplanExcel";
import { buildShiftplanPdfLayout, downloadShiftplanPdf } from "./pdf/buildShiftplanPdf";
import { downloadWiesnshopExcel } from "../../api/shiftplanApi";

const VIEW_MODES = Object.freeze({ WEEK: "week", MONTH: "month" });
/*
  Der Export startet immer auf dem Monat, unabhängig davon, was die Seite gerade
  zeigt. Die Tagesspalte ist dort nur rund 20px breit – in der Zelle steht darum
  nur Farbe und Kürzel, aufgelöst über die Legende.
*/
const DEFAULT_VIEW_MODE = VIEW_MODES.MONTH;
const FOOTER_TEXT_MAX_LENGTH = 120;

const DEFAULT_SETTINGS = Object.freeze({
  includeAbsences: true,
  includeHolidays: true,
  releasedOnly: false,
  orientation: "landscape",
  colorMode: "color",
  includeLegend: true,
  footerText: "",
  cellDisplay: { ...DEFAULT_CELL_DISPLAY },
});

/*
  Segment-Umschalter für zwei bis drei gleichrangige Modi. Das DS hat dafür
  keine Komponente (AppTabs sind Unterstreichungs-Reiter für Seitenbereiche),
  darum bleibt die Form lokal – Farben, Radien und Schrift kommen aus dem DS.
  Gleiche Bauweise wie der Umschalter im Schicht-Editor.
*/
function SegmentSwitch({ ariaLabel, options, value, onChange }) {
  return (
    <div className="spv2-export-segments" role="group" aria-label={ariaLabel}>
      {options.map((option) => {
        const isActive = option.value === value;

        return (
          <button
            key={option.value}
            type="button"
            aria-pressed={isActive}
            className={`spv2-export-segment${isActive ? " is-active" : ""}`}
            onClick={() => onChange(option.value)}
          >
            {option.icon || null}
            <span>{option.label}</span>
          </button>
        );
      })}
    </div>
  );
}

/*
  Der Plan bleibt immer auf einer Seite und wird dafür bei vielen Zeilen
  kleiner. Was dann hilft: das Hochformat (mehr Höhe für Zeilen) und eine
  kleinere Auswahl – im Hochformat bleibt nur noch die Auswahl.
*/
function buildSmallTextHint(orientation) {
  const remedy =
    orientation === "portrait"
      ? "Mit weniger Mitarbeitenden wird sie grösser."
      : "Im Hochformat oder mit weniger Mitarbeitenden wird sie grösser.";
  return `Für eine Seite sind es sehr viele Zeilen – die Schrift wird dadurch sehr klein. ${remedy}`;
}

function SettingsSection({ title, hint, children }) {
  return (
    <section className="spv2-export-section">
      <AppText as="h4" variant="label-3" tone="secondary" className="spv2-export-section-title">
        {title}
      </AppText>
      {hint ? (
        <AppText variant="body-3" tone="tertiary" className="spv2-export-section-hint">
          {hint}
        </AppText>
      ) : null}
      <div className="spv2-export-section-body">{children}</div>
    </section>
  );
}

function EmployeeSelection({ employees, selectedIds, allSelected, onAllSelectedChange, onToggleEmployee }) {
  const [query, setQuery] = useState("");
  const normalizedQuery = query.trim().toLocaleLowerCase("de");
  const filteredEmployees = useMemo(
    () =>
      employees.filter((employee) =>
        !normalizedQuery ? true : employee.name.toLocaleLowerCase("de").includes(normalizedQuery)
      ),
    [employees, normalizedQuery]
  );

  return (
    <>
      <AppSwitch
        label={`Alle Mitarbeitenden (${employees.length})`}
        checked={allSelected}
        onChange={(event) => onAllSelectedChange(event.target.checked)}
      />
      {!allSelected ? (
        <div className="spv2-export-employee-picker">
          <AppInput
            size="sm"
            type="search"
            clearable
            placeholder="Mitarbeitende suchen"
            aria-label="Mitarbeitende suchen"
            value={query}
            onChange={(event) => setQuery(String(event.target?.value ?? ""))}
          />
          <div className="spv2-export-employee-list" role="group" aria-label="Mitarbeitende auswählen">
            {filteredEmployees.length ? (
              filteredEmployees.map((employee) => (
                <AppCheckbox
                  key={employee.id}
                  className="spv2-export-employee-row"
                  label={employee.name}
                  checked={selectedIds.has(employee.id)}
                  onChange={() => onToggleEmployee(employee.id)}
                />
              ))
            ) : (
              <AppText variant="body-3" tone="tertiary" className="spv2-export-employee-empty">
                Keine Mitarbeitenden gefunden
              </AppText>
            )}
          </div>
        </div>
      ) : null}
    </>
  );
}

export default function ShiftplanExportDialog({
  open,
  initialFormat = EXPORT_FORMATS.PDF,
  referenceDate,
  employees = [],
  shifts = [],
  userHolidays = [],
  publicHolidayNamesByDate = {},
  onLoadPlanRange,
  // Steht im PDF-Kopf neben "SCHICHTPLAN" – der Aushang sagt, wessen Plan er ist.
  companyName = "",
  wiesnshopExcelExport = false,
  onClose,
}) {
  const [format, setFormat] = useState(normalizeExportFormat(initialFormat));
  const [exportViewMode, setExportViewMode] = useState(DEFAULT_VIEW_MODE);
  const [exportReferenceDate, setExportReferenceDate] = useState(referenceDate || new Date());
  const [settings, setSettings] = useState(DEFAULT_SETTINGS);
  const [allEmployeesSelected, setAllEmployeesSelected] = useState(true);
  const [selectedEmployeeIds, setSelectedEmployeeIds] = useState(() => new Set());
  const [isDownloading, setIsDownloading] = useState(false);
  const [error, setError] = useState("");

  const exportableEmployees = useMemo(
    () =>
      (Array.isArray(employees) ? employees : [])
        .filter((employee) => employee?.id && !employee.isPlaceholder)
        .map((employee) => ({ id: String(employee.id), name: employee.name || "Unbenannt" })),
    [employees]
  );

  useEffect(() => {
    if (!open) return;
    setFormat(normalizeExportFormat(initialFormat));
    setExportViewMode(DEFAULT_VIEW_MODE);
    setExportReferenceDate(referenceDate || new Date());
    setSettings({ ...DEFAULT_SETTINGS, cellDisplay: { ...DEFAULT_CELL_DISPLAY } });
    setAllEmployeesSelected(true);
    setSelectedEmployeeIds(new Set(exportableEmployees.map((employee) => employee.id)));
    setError("");
  }, [exportableEmployees, initialFormat, open, referenceDate]);

  const isWeek = exportViewMode === VIEW_MODES.WEEK;
  const days = useMemo(
    () => (isWeek ? getWeekDays(exportReferenceDate) : getMonthDays(exportReferenceDate)),
    [exportReferenceDate, isWeek]
  );
  const periodLabel = useMemo(
    () => (isWeek ? formatWeekTitle(exportReferenceDate) : formatMonthTitle(exportReferenceDate)),
    [exportReferenceDate, isWeek]
  );

  /*
    Im Dialog lässt sich zu jeder Periode blättern, auch weg von der, die das
    Raster zeigt. Da der Plan nur den Sichtbereich lädt, holt der Export den
    gewählten Zeitraum selbst — sonst wäre ein Export des Vormonats leer.
  */
  const exportRange = usePlanRange({
    enabled: open,
    startDate: days[0]?.key || "",
    endDate: days[days.length - 1]?.key || "",
    onLoadPlanRange,
    fallbackShifts: shifts,
    fallbackUserHolidays: userHolidays,
  });
  const exportPublicHolidayNamesByDate = useMemo(
    () => (exportRange.isFallback
      ? publicHolidayNamesByDate
      : groupPublicHolidayNamesByDate(exportRange.publicHolidays)),
    [exportRange.isFallback, exportRange.publicHolidays, publicHolidayNamesByDate]
  );

  const exportModel = useMemo(
    () =>
      buildShiftplanExportModel({
        employees,
        shifts: exportRange.shifts,
        userHolidays: exportRange.userHolidays,
        days,
        publicHolidayNamesByDate: exportPublicHolidayNamesByDate,
        options: {
          includeAbsences: settings.includeAbsences,
          includeHolidays: settings.includeHolidays,
          releasedOnly: settings.releasedOnly,
          employeeIds: allEmployeesSelected ? null : Array.from(selectedEmployeeIds),
          cellDisplay: settings.cellDisplay,
        },
      }),
    [
      allEmployeesSelected,
      days,
      employees,
      exportPublicHolidayNamesByDate,
      exportRange.shifts,
      exportRange.userHolidays,
      selectedEmployeeIds,
      settings.cellDisplay,
      settings.includeAbsences,
      settings.includeHolidays,
      settings.releasedOnly,
    ]
  );

  const updateSettings = useCallback((patch) => {
    setSettings((current) => ({ ...current, ...patch }));
  }, []);

  const updateCellDisplay = useCallback((patch) => {
    setSettings((current) => ({ ...current, cellDisplay: { ...current.cellDisplay, ...patch } }));
  }, []);

  const handlePreviousPeriod = useCallback(() => {
    setExportReferenceDate((current) => (isWeek ? addWeeksToDate(current, -1) : addMonthsToDate(current, -1)));
  }, [isWeek]);

  const handleNextPeriod = useCallback(() => {
    setExportReferenceDate((current) => (isWeek ? addWeeksToDate(current, 1) : addMonthsToDate(current, 1)));
  }, [isWeek]);

  const toggleEmployee = useCallback((employeeId) => {
    setSelectedEmployeeIds((current) => {
      const next = new Set(current);
      if (next.has(employeeId)) {
        next.delete(employeeId);
      } else {
        next.add(employeeId);
      }
      return next;
    });
  }, []);

  const handleAllEmployeesChange = useCallback(
    (checked) => {
      setAllEmployeesSelected(checked);
      if (!checked && !selectedEmployeeIds.size) {
        setSelectedEmployeeIds(new Set(exportableEmployees.map((employee) => employee.id)));
      }
    },
    [exportableEmployees, selectedEmployeeIds.size]
  );

  const isPdf = format === EXPORT_FORMATS.PDF;
  /*
    Saison-Sonderfall Wiesnshop: Excel kommt fertig vom Server (altes Format
    mit Bedarfsansicht, immer alle Mitarbeitenden). Die Client-Optionen und
    die Vorschau greifen dann nicht und werden durch einen Hinweis ersetzt.
  */
  const isServerExcel = !isPdf && Boolean(wiesnshopExcelExport);
  /*
    Das Seitenlayout des PDFs – dieselbe Rechnung wie beim Zeichnen. Im Monat
    ist die Tagesspalte zu schmal für Text, dort steht nur Farbe und Kürzel:
    Die Zellinhalt-Schalter greifen dann nicht und sind darum gesperrt statt
    wirkungslos. Excel hat breite Spalten und bleibt frei.
  */
  const pdfLayout = useMemo(
    () =>
      isPdf
        ? buildShiftplanPdfLayout({
            model: exportModel,
            settings: { orientation: settings.orientation, includeLegend: settings.includeLegend },
          })
        : null,
    [exportModel, isPdf, settings.includeLegend, settings.orientation]
  );
  const usesCodeCells = Boolean(pdfLayout?.isCompact);
  const hasRows = exportModel.rows.length > 0;
  const hasEntries =
    exportModel.stats.shiftCount + exportModel.stats.absenceCount + exportModel.stats.holidayCount > 0;
  // Die Vorschau ist zu klein, als dass man dort sähe, wie klein die Schrift wird.
  const smallTextHint =
    pdfLayout && hasRows && !pdfLayout.isTextLegible ? buildSmallTextHint(settings.orientation) : "";
  const cellDisplay = settings.cellDisplay;

  const summaryLabel = useMemo(() => {
    const parts = [
      `${exportModel.stats.shiftCount} Schichten`,
      settings.includeAbsences ? `${exportModel.stats.absenceCount} Absenzen` : "",
      settings.includeHolidays ? `${exportModel.stats.holidayCount} Ferien` : "",
    ].filter(Boolean);
    return `${exportModel.rows.length} Mitarbeitende · ${parts.join(" · ")}`;
  }, [exportModel.rows.length, exportModel.stats, settings.includeAbsences, settings.includeHolidays]);

  const handleDownload = useCallback(async () => {
    if (isServerExcel) {
      const startDate = days[0]?.key || "";
      const endDate = days[days.length - 1]?.key || "";
      setIsDownloading(true);
      setError("");
      try {
        const blob = await downloadWiesnshopExcel({ startDate, endDate });
        const url = window.URL.createObjectURL(blob);
        const link = document.createElement("a");
        link.href = url;
        link.download = `Einsatzplan_${startDate}_${endDate}.xlsx`;
        document.body.appendChild(link);
        link.click();
        link.remove();
        window.URL.revokeObjectURL(url);
      } catch (downloadError) {
        setError("Excel-Datei konnte nicht erstellt werden.");
      } finally {
        setIsDownloading(false);
      }
      return;
    }

    if (!hasRows) {
      setError("Keine Mitarbeitenden für den Export ausgewählt.");
      return;
    }

    setIsDownloading(true);
    setError("");
    try {
      if (isPdf) {
        await downloadShiftplanPdf({ model: exportModel, settings, periodLabel, companyName });
      } else {
        await downloadShiftplanExcel({ model: exportModel, settings, periodLabel });
      }
    } catch (downloadError) {
      setError(isPdf ? "PDF konnte nicht erstellt werden." : "Excel-Datei konnte nicht erstellt werden.");
    } finally {
      setIsDownloading(false);
    }
  }, [companyName, days, exportModel, hasRows, isPdf, isServerExcel, periodLabel, settings]);

  const downloadLabel = isDownloading
    ? "Wird erstellt …"
    : isPdf
      ? "PDF herunterladen"
      : "Excel herunterladen";

  return (
    <ShiftplanDialog
      open={open}
      title="Schichtplan exportieren"
      subtitle={periodLabel}
      icon={<FileDownloadOutlinedIcon fontSize="small" />}
      // Die Vorschau soll ein ganzes Monatsraster zeigen – dafür ist xl (1180px)
      // die einzige Breite, in der die Spalten nicht zu Strichen werden.
      size="xl"
      bodyClassName="spv2-export-dialog-body"
      footerStart={
        <AppText variant="body-2" tone="secondary" truncate className="spv2-export-summary">
          {isServerExcel ? periodLabel : summaryLabel}
        </AppText>
      }
      footerActions={
        // Nicht herunterladen, solange der gewählte Zeitraum noch geladen wird
        // oder sein Laden fehlgeschlagen ist: In beiden Fällen steht in der
        // Vorschau die angezeigte Periode als Ersatz, und die Datei enthielte
        // den falschen Monat unter dem Label des gewählten. Der Server-Export
        // lädt seinen Zeitraum selbst und hängt nicht am Vorschau-Zustand.
        <AppButton
          variant="primary"
          disabled={
            isServerExcel
              ? isDownloading
              : isDownloading || !hasRows || exportRange.isLoading || Boolean(exportRange.error)
          }
          onClick={handleDownload}
        >
          <FileDownloadOutlinedIcon fontSize="small" />
          {downloadLabel}
        </AppButton>
      }
      onClose={onClose}
    >
      <div className="spv2-export-layout">
        <div className="spv2-export-settings">
          <SettingsSection title="Format">
            <SegmentSwitch
              ariaLabel="Exportformat"
              value={format}
              onChange={setFormat}
              options={[
                {
                  value: EXPORT_FORMATS.PDF,
                  label: "PDF",
                  icon: <PictureAsPdfOutlinedIcon fontSize="small" />,
                },
                {
                  value: EXPORT_FORMATS.EXCEL,
                  label: "Excel",
                  icon: <GridOnOutlinedIcon fontSize="small" />,
                },
              ]}
            />
          </SettingsSection>

          <SettingsSection title="Zeitraum">
            <SegmentSwitch
              ariaLabel="Zeitraumart"
              value={exportViewMode}
              onChange={setExportViewMode}
              options={[
                { value: VIEW_MODES.WEEK, label: "Woche" },
                { value: VIEW_MODES.MONTH, label: "Monat" },
              ]}
            />
            <div className="spv2-export-period-nav">
              <AppButton
                iconOnly
                size="sm"
                variant="muted"
                aria-label={isWeek ? "Vorherige Woche" : "Vorheriger Monat"}
                onClick={handlePreviousPeriod}
              >
                <IconChevronLeft sx={{ fontSize: 18 }} />
              </AppButton>
              <AppText variant="label-2" truncate className="spv2-export-period-label">
                {periodLabel}
              </AppText>
              <AppButton
                iconOnly
                size="sm"
                variant="muted"
                aria-label={isWeek ? "Nächste Woche" : "Nächster Monat"}
                onClick={handleNextPeriod}
              >
                <IconChevronRight sx={{ fontSize: 18 }} />
              </AppButton>
            </div>
          </SettingsSection>

          {!isServerExcel ? (
          <SettingsSection
            title="Zellinhalt"
            hint={usesCodeCells ? "Im Monat trägt die Zelle nur Farbe und Kürzel." : ""}
          >
            <AppSwitch
              label="Zeit"
              disabled={usesCodeCells}
              checked={cellDisplay.showTime}
              onChange={(event) => updateCellDisplay({ showTime: event.target.checked })}
            />
            <AppSwitch
              label="Abteilung"
              disabled={usesCodeCells}
              checked={cellDisplay.showDepartment}
              onChange={(event) => updateCellDisplay({ showDepartment: event.target.checked })}
            />
            <AppSwitch
              label="Schicht"
              disabled={usesCodeCells}
              checked={cellDisplay.showShift}
              onChange={(event) => updateCellDisplay({ showShift: event.target.checked })}
            />
            <AppSwitch
              label="Abkürzungen"
              description="Kürzel statt voller Namen"
              disabled={usesCodeCells}
              checked={cellDisplay.useAbbreviations}
              onChange={(event) => updateCellDisplay({ useAbbreviations: event.target.checked })}
            />
            {/* Die Legende löst auf, was in der Zelle steht – sie gehört hierher
                und bleibt auch im Monat schaltbar, wo sie das Kürzel erklärt. */}
            <AppSwitch
              label="Legende anzeigen"
              checked={settings.includeLegend}
              onChange={(event) => updateSettings({ includeLegend: event.target.checked })}
            />
          </SettingsSection>
          ) : null}

          {!isServerExcel ? (
          <SettingsSection title="Inhalt">
            <AppSwitch
              label="Absenzen"
              checked={settings.includeAbsences}
              onChange={(event) => updateSettings({ includeAbsences: event.target.checked })}
            />
            <AppSwitch
              label="Ferien"
              checked={settings.includeHolidays}
              onChange={(event) => updateSettings({ includeHolidays: event.target.checked })}
            />
            <AppSwitch
              label="Nur freigegebene Schichten"
              description="Entwürfe werden ausgeblendet"
              checked={settings.releasedOnly}
              onChange={(event) => updateSettings({ releasedOnly: event.target.checked })}
            />
          </SettingsSection>
          ) : null}

          {!isServerExcel ? (
          <SettingsSection title="Mitarbeitende">
            <EmployeeSelection
              employees={exportableEmployees}
              selectedIds={selectedEmployeeIds}
              allSelected={allEmployeesSelected}
              onAllSelectedChange={handleAllEmployeesChange}
              onToggleEmployee={toggleEmployee}
            />
          </SettingsSection>
          ) : null}

          {!isServerExcel ? (
          <SettingsSection title="Darstellung">
            {isPdf ? (
              <SegmentSwitch
                ariaLabel="Seitenformat"
                value={settings.orientation}
                onChange={(orientation) => updateSettings({ orientation })}
                options={[
                  {
                    value: "landscape",
                    label: "Quer",
                    icon: <CropLandscapeOutlinedIcon fontSize="small" />,
                  },
                  {
                    value: "portrait",
                    label: "Hoch",
                    icon: <CropPortraitOutlinedIcon fontSize="small" />,
                  },
                ]}
              />
            ) : null}
            <SegmentSwitch
              ariaLabel="Farbmodus"
              value={settings.colorMode}
              onChange={(colorMode) => updateSettings({ colorMode })}
              options={[
                { value: "color", label: "Farbig" },
                { value: "mono", label: "Schwarzweiss" },
              ]}
            />
          </SettingsSection>
          ) : null}

          {/* Nur das PDF hat eine Fusszeile – die Excel-Datei kennt keine. */}
          {isPdf ? (
            <SettingsSection title="Fusstext">
              <AppInput
                size="sm"
                placeholder="Individueller Fusstext"
                aria-label="Individueller Fusstext"
                value={settings.footerText}
                maxLength={FOOTER_TEXT_MAX_LENGTH}
                onChange={(event) =>
                  updateSettings({ footerText: String(event.target.value).slice(0, FOOTER_TEXT_MAX_LENGTH) })
                }
              />
            </SettingsSection>
          ) : null}
        </div>

        <div className="spv2-export-preview-pane">
          {!isServerExcel && exportRange.error ? (
            <div className="spv2-export-error" role="alert">
              Der gewählte Zeitraum konnte nicht geladen werden — der Download ist gesperrt, damit
              nicht der falsche Monat exportiert wird. Prüfe deine Verbindung und öffne den Dialog erneut.
            </div>
          ) : null}
          {error ? (
            <div className="spv2-export-error" role="alert">
              {error}
            </div>
          ) : null}
          {isServerExcel ? (
            <div className="spv2-export-server-state" role="note">
              <div className="spv2-export-server-card">
                <div className="spv2-export-server-icon">
                  <GridOnOutlinedIcon />
                </div>
                <AppText as="h4" variant="heading-5" className="spv2-export-server-title">
                  Excel mit Bedarfsansicht
                </AppText>
                <AppText variant="body-3" tone="secondary" className="spv2-export-server-lead">
                  Die Datei wird beim Herunterladen im gewohnten Format erstellt und enthält:
                </AppText>
                <ul className="spv2-export-server-list">
                  <li>
                    <CheckOutlinedIcon fontSize="inherit" />
                    <span>Tagesansicht mit Früh-, Mittag- und Abend-Färbung</span>
                  </li>
                  <li>
                    <CheckOutlinedIcon fontSize="inherit" />
                    <span>„Fehlende Planung": offener Personalbedarf pro Tag und Zeitblock</span>
                  </li>
                  <li>
                    <CheckOutlinedIcon fontSize="inherit" />
                    <span>Alle Mitarbeitenden des gewählten Zeitraums</span>
                  </li>
                </ul>
              </div>
            </div>
          ) : (
            <>
              <ExportPreview
                format={format}
                model={exportModel}
                settings={settings}
                periodLabel={periodLabel}
                companyName={companyName}
              />
              {!hasEntries ? (
                <p className="spv2-export-preview-hint">
                  Im gewählten Zeitraum sind keine Einträge vorhanden — exportiert wird ein leeres Raster.
                </p>
              ) : null}
              {smallTextHint ? <p className="spv2-export-preview-hint">{smallTextHint}</p> : null}
            </>
          )}
        </div>
      </div>
    </ShiftplanDialog>
  );
}
